import Cookies from 'js-cookie';
import { NavigateFunction } from 'react-router-dom';
import { action, computed, makeObservable, observable } from 'mobx';
import { format } from 'date-fns';

import { AsyncState } from 'shared/libs/state';
import {
   MainAnalyticsChartDataItem,
   MainAnalyticsData,
   MainAnalyticsRequest,
   MainAnalyticsResponse,
} from './main-analytics.types';
import { MainAnalyticsApiService } from './main-analytics.api-service';

export type MainAnalyticsChartContext = 'cv' | 'simple_cv' | 'registrations' | 'jobs';

export class MainAnalyticsStore {
   readonly loadAnalyticsState = new AsyncState<MainAnalyticsResponse>();
   chartContext: MainAnalyticsChartContext = 'cv';
   dateFrom: Date | null = null;
   dateTo: Date | null = null;

   constructor(private readonly navigate: NavigateFunction) {
      makeObservable(this, {
         chartContext: observable,
         dateFrom: observable,
         dateTo: observable,
         data: computed,
         chartData: computed,
         isLoading: computed,
         isError: computed,
         setChartContext: action.bound,
         setDateFrom: action.bound,
         setDateTo: action.bound,
      });
   }

   get data(): MainAnalyticsData | null {
      return this.loadAnalyticsState.data?.response ?? null;
   }

   get chartData(): MainAnalyticsChartDataItem[] {
      return this.data?.chart_data ?? [];
   }

   get isLoading(): boolean {
      return this.loadAnalyticsState.status === 'idle' || this.loadAnalyticsState.status === 'pending';
   }

   get isError(): boolean {
      return this.loadAnalyticsState.status === 'error';
   }

   setChartContext(context: MainAnalyticsChartContext): void {
      this.chartContext = context;
   }

   setDateFrom(dateFrom: Date | null): void {
      this.dateFrom = dateFrom;

      if (dateFrom && this.dateTo && dateFrom > this.dateTo) {
         this.dateTo = null;
      }
   }

   setDateTo(dateTo: Date | null): void {
      this.dateTo = dateTo;
   }

   async loadAnalytics(): Promise<void> {
      const tokenKey = Cookies.get('token');

      if (!tokenKey) {
         this.navigate('/admin');
         return;
      }

      await this.loadAnalyticsState.run(() => MainAnalyticsApiService.load(this.buildPayload()));
   }

   private buildPayload(): MainAnalyticsRequest {
      return {
         from: this.dateFrom ? format(this.dateFrom, 'yyyy-MM-dd') : null,
         to: this.dateTo ? format(this.dateTo, 'yyyy-MM-dd') : null,
      };
   }
}
